import { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { deleteAccount } from "src/services/accountService";
import AuthService from "src/services/authService";
import { makeStyles } from "tss-react/mui";

import { Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { DangerButton, SecondaryButton } from "@shared/CustomButton";
import PasswordField from "@shared/form/PasswordField";
import { SectionFormStack, SectionInputSlot } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme: Theme) => ({
    description: {
        marginBottom: theme.spacing(1.5),
    },
    error: {
        color: theme.palette.emteria.error,
        fontWeight: 600,
    },
}));

type FormValues = {
    email: string | undefined;
    password: string;
};

type DeleteAccountFormProps = {
    onCancel: () => void;
};

export const DeleteAccountForm = ({ onCancel }: DeleteAccountFormProps) => {
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState<string>();
    const navigate = useNavigate();
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const onValidate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.password) {
            errors.password = t("pages:deleteAccountPage.validation.passwordRequired");
        }

        return errors;
    };

    const onSubmit = async (values: FormValues) => {
        try {
            setLoading(true);
            values.email = email;

            const { message } = await deleteAccount(values);
            showSuccessToast(message);
            void navigate("/logout");
        } catch (err) {
            if (err instanceof Error) {
                showErrorToast(err.message);
            } else {
                showErrorToast(String(err));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const _user = AuthService.getCurrentUser();
        setEmail(_user?.email);
    }, []);

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit}>
                    <SectionPanel
                        title={t("pages:deleteAccountPage.title")}
                        actions={
                            <>
                                <SecondaryButton type="button" onClick={onCancel} disabled={loading}>
                                    {t("common:button.cancel")}
                                </SecondaryButton>
                                <DangerButton type="submit" disabled={loading} loading={loading}>
                                    {t("common:button.delete")}
                                </DangerButton>
                            </>
                        }
                    >
                        <Typography className={classes.description}>
                            <Trans
                                ns="pages"
                                i18nKey="deleteAccountPage.warning"
                                values={{ email }}
                                components={{ bold: <b className={classes.error} /> }}
                            />
                        </Typography>
                        <SectionFormStack>
                            <SectionInputSlot>
                                <PasswordField
                                    name="password"
                                    label={t("pages:deleteAccountPage.passwordPlaceholder")}
                                    fullWidth
                                />
                            </SectionInputSlot>
                        </SectionFormStack>
                    </SectionPanel>
                </form>
            )}
        />
    );
};
